import type { SalesChecklist } from "@expert/contentful";
import type { Logger } from "@expert/logging";

import { useCallback, useEffect, useRef, useState } from "react";

import { DynamicSmartOffer } from "../DynamicSmartOffer";
import { useDynamicSmartOfferStore } from "../store/useDynamicSmartOfferStore";

interface Dispatcher {
    dispatchBusinessEvent: (event: string, payload?: Record<string, unknown>) => void | Promise<void>;
}

const FETCH_DELAY_MS = 2000;

export function useDynamicSmartOffer({
    sessionId,
    salesChecklist,
    partner,
    dispatcher,
    logger,
    isDynamicSmartOfferEnabled = false,
}: {
    sessionId: string;
    salesChecklist: SalesChecklist;
    partner: string;
    dispatcher: Dispatcher;
    logger: Logger;
    isDynamicSmartOfferEnabled?: boolean;
}) {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hasDispatchedRef = useRef(false);

    const [isPending, setIsPending] = useState(false);
    const [data, setData] = useState<DynamicSmartOffer>(new DynamicSmartOffer("", []));

    const isEnabled = isDynamicSmartOfferEnabled && hasCoreBenefitsWithDynamicContent(salesChecklist);

    // Add a delay to simulate a fetch
    const fetch = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        setIsPending(true);
        timerRef.current = setTimeout(() => {
            const latestData = useDynamicSmartOfferStore.getState().getSession(sessionId).latestData;
            logger.info({ sessionId, isValid: latestData.isValid() }, "Dynamic smart offer timer resolved");
            setData(latestData);
            setIsPending(false);
        }, FETCH_DELAY_MS);
    }, [sessionId, logger]);

    useEffect(() => {
        fetch();
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [fetch]);

    const storeLatestData = useDynamicSmartOfferStore((s) => s.getSession(sessionId).latestData);

    const showLatest = useCallback(() => {
        void dispatcher.dispatchBusinessEvent("DynamicSmartOfferShowLatestClicked", { sessionId, partner });
        fetch();
    }, [fetch, sessionId, partner, dispatcher]);

    const isFallback = storeLatestData.isFallback();
    const isAwaitingData = !isFallback && !isPending && !data.isValid();
    // Fallback events bypass the "New pitch available" indicator — they display immediately
    // via `isFallback` rather than gating behind a click.
    const hasNewerData = !isFallback && !isPending && storeLatestData.isValid() && !storeLatestData.equals(data);

    // When awaiting initial data and an event arrives, auto-fetch (with the standard delay).
    useEffect(() => {
        if (isAwaitingData && hasNewerData) {
            logger.info({ sessionId }, "Dynamic smart offer auto-fetch triggered");
            fetch();
        }
    }, [isAwaitingData, hasNewerData, fetch, logger, sessionId]);

    // Honor a CLEAR of the store, not just a new offer.
    //
    // Everything above that refreshes the rendered `data` — `hasNewerData` and the auto-fetch
    // it drives — is gated on the incoming data being VALID. An emptied store is invalid by
    // definition, so a deliberate clear could never propagate: the previously-rendered pitch
    // point stayed on screen until the component remounted. That is why the Event Emitter's
    // Reset button appeared to do nothing at all.
    //
    // Clearing immediately (rather than through `fetch`) is deliberate: the delay exists to
    // simulate generation latency for arriving content, and there is nothing to wait for when
    // removing it.
    useEffect(() => {
        if (data.isValid() && !storeLatestData.isValid() && !storeLatestData.isFallback()) {
            logger.info({ sessionId }, "Dynamic smart offer cleared");
            if (timerRef.current) clearTimeout(timerRef.current);
            setIsPending(false);
            setData(storeLatestData);
            // The "shown" event is once-per-offer, not once-per-mount. Clearing ends the current
            // offer's life, so the guard has to reopen or the NEXT offer would render silently.
            hasDispatchedRef.current = false;
        }
    }, [data, storeLatestData, logger, sessionId]);

    // Fire once when dynamic content is first rendered — i.e., when data transitions from
    // invalid to valid after the pending delay. This confirms the expert actually saw the
    // generated offer, not just that we received the WS event.
    //
    // ⚠️ Do NOT add `storeLatestData` to this dep array. The clear effect above reopens the
    // guard (`hasDispatchedRef.current = false`) synchronously, and it fires on the render
    // where only `storeLatestData` changed — `data` is still the outgoing, still-valid offer
    // at that point. This effect is safe today precisely because that render does not
    // re-invoke it: its deps are unchanged, so it never observes the reopened guard while
    // `data.isValid()` is still true. Depending on `storeLatestData` would put both effects in
    // the same flush and re-dispatch "shown" for the offer being cleared — and, worse, leave
    // the guard set so the NEXT real offer went silent. Covered by "does not re-fire
    // DynamicSmartOfferShown for an offer that is being cleared".
    useEffect(() => {
        if (!hasDispatchedRef.current && data.isValid()) {
            hasDispatchedRef.current = true;
            void dispatcher.dispatchBusinessEvent("DynamicSmartOfferShown", { sessionId, partner });
        }
    }, [data, sessionId, partner, dispatcher]);

    return { data, isPending, isAwaitingData, isEnabled, showLatest, hasNewerData, isFallback };
}

function hasCoreBenefitsWithDynamicContent(salesChecklist: SalesChecklist) {
    return salesChecklist.checklistSections.some((s) => s.sectionType === "coreBenefits" && s.enableDynamicContent);
}
